import React, { useState } from 'react';
import { FilterDataType } from '../types/filterTypes';
import DateRangePicker from './common/DateRangePicker';
import TimeRangePicker from './common/TimeRangePicker';
import '../styles/components/MultiValueFilter.css';

interface FilterProps {
  label: string;
  type: FilterDataType;
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>; // For enum types
  isMultiValue?: boolean;
  filterKey?: string; // For multiValue handlers
}

const Filter: React.FC<FilterProps> = ({
  label,
  type,
  value,
  onChange,
  placeholder = '',
  options = [],
  isMultiValue = false,
  filterKey = ''
}) => {
  // Move useState to component top level
  const [inputValue, setInputValue] = useState('');
  
  // Handle multi-value case differently
  if (isMultiValue) {
    const values = Array.isArray(value) ? value : value ? [value] : [];

    const handleAddValue = (e: React.FormEvent) => {
      e.preventDefault();
      if (inputValue.trim()) {
        const newValues = [...values, inputValue.trim()];
        onChange(newValues);
        setInputValue('');
      }
    };

    const handleRemoveValue = (valueToRemove: string) => {
      const newValues = values.filter(v => v !== valueToRemove);
      onChange(newValues);
    };
    /* Multi Value Filter is directly rendered here. The MultiValueFilter.tsx is hence redundant, should be removed */
    return (
      <div className="multi-value-filter">
        <h4>{label}</h4>
        <div className="filter-values">
          {values.map((val, index) => (
            <div className="filter-value-tag" key={index}>
              <span>{val}</span>
              <button 
                className="remove-value-button" 
                onClick={() => handleRemoveValue(val)}
              >
                <span className="material-icons">close</span>
              </button>
            </div>
          ))}
        </div>
        <form onSubmit={handleAddValue} className="add-value-form">
            <div className="add-value-row">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={placeholder || `Add ${label.toLowerCase()}`}
                    className="filter-input"
                />
                <button type="submit" className="add-value-button">
                    <span className="material-icons">add</span>
                </button>
          </div>
        </form>
      </div>
    );
  }

  // Render appropriate input based on type
  return (
    <div className="filter-row">
      <div className="filter-label">{label}</div>
      {renderInput()}
    </div>
  );

  // Render the appropriate input element based on type
  function renderInput() {
    switch (type) {
      case 'string':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || `Enter ${label.toLowerCase()}`}
            className="filter-input"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={placeholder || `Enter ${label.toLowerCase()}`}
            className="filter-input"
          />
        );

      case 'date':
        if (isMultiValue) {
          // For multi-value date, we would need a more complex control
          // This is a simplified placeholder implementation
          return (
            <input
              type="date"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="filter-input"
            />
          );
        }
        
        // For date ranges, use the new component
        return (
          <DateRangePicker
            startDate={value.min || ''}
            endDate={value.max || ''}
            onStartDateChange={(date) => onChange({...value, min: date})}
            onEndDateChange={(date) => onChange({...value, max: date})}
            isSingleDate={false}
          />
        );
        
      case 'time':
        if (isMultiValue) {
          // For multi-value time, we would need a more complex control
          // This is a simplified placeholder implementation
          return (
            <input
              type="time"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="filter-input"
            />
          );
        }
        
        // For time ranges, use the new component
        return (
          <TimeRangePicker
            startTime={value.min || ''}
            endTime={value.max || ''}
            onStartTimeChange={(time) => onChange({...value, min: time})}
            onEndTimeChange={(time) => onChange({...value, max: time})}
            isSingleTime={false}
          />
        );

      case 'boolean':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value === 'true')}
            className="filter-input"
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      case 'enum':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="filter-input"
          >
            <option value="">Select {label}...</option>
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || `Enter ${label.toLowerCase()}`}
            className="filter-input"
          />
        );
    }
  }
};

export default Filter; 